import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Building2, Plus, Search } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { formatDayLabel } from '@/lib/dates'
import { useClientDashboards } from '@/queries/clientDashboards'
import { useUnplacedMeetings } from '@/queries/clientMeetings'
import { nextCallLabel } from '@/types/clientMeetings'
import { CADENCE_LABELS, memberName } from '@/types/clientDashboard'
import type { ClientDashboard } from '@/types/clientDashboard'
import { AddClientDialog } from './AddClientDialog'
import { AssignMeetingDialog, needsAssigningLabel } from './AssignMeetingDialog'
import { ChurnPill, PercentPill } from './HealthPills'
import { LoadError } from './LoadError'
import { RenewalPill } from './RenewalPill'
import { CLIENTS_PAGE, clientTabPath } from './tabs'

const filterSelectClass =
  'h-[var(--etn-control-height-input)] shrink-0 rounded-etn-pill border border-etn-border-input bg-etn-surface px-4 text-etn-control text-etn-text-cell'

const thClass =
  'h-9 whitespace-nowrap border-b border-[var(--etn-gray-100)] px-4 text-left text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label'

const tableCardClass = 'overflow-x-auto rounded-etn-card border border-etn-border-hairline bg-etn-surface shadow-etn-whisper'

const tdClass = 'h-11 whitespace-nowrap px-4 py-2.5 align-middle'

const COLUMNS = ['Client', 'Health score', 'Churn risk', 'Account team', 'Open', 'Triage', 'Awaiting client', 'Not logged', 'Last call', 'Next call', 'Cadence', 'SLA renewal']

const NotSet = () => <span className="italic text-etn-text-muted">Not set</span>
const Pending = () => <span className="text-etn-text-muted">-</span>

function ClientRow({ dashboard }: { dashboard: ClientDashboard }) {
  const summary = dashboard.kpiSummary
  return (
    <tr className="hover:bg-etn-row-hover">
      <td className={`${tdClass} font-medium text-etn-text`}>
        <Link to={clientTabPath(dashboard.companyId, 'overview') as never} className="hover:text-etn-accent">
          {dashboard.companyName}
        </Link>
        {dashboard.companyDeleted && (
          <Badge variant="destructive" className="ml-2">
            Company deleted
          </Badge>
        )}
      </td>
      <td className={tdClass}>{summary ? <PercentPill percent={summary.healthPercent} band={summary.healthBand} /> : <Pending />}</td>
      <td className={tdClass}>{summary ? <ChurnPill churn={summary.churn} /> : <Pending />}</td>
      <td className={`${tdClass} text-etn-text-soft`}>
        {dashboard.accountTeam.length ? dashboard.accountTeam.map(memberName).join(', ') : <NotSet />}
      </td>
      {(['open', 'triage', 'awaiting', 'notLogged'] as const).map(column => (
        <td key={column} className={`${tdClass} tabular-nums`}>
          {summary?.counts[column] ?? <Pending />}
        </td>
      ))}
      <td className={`${tdClass} text-etn-text-soft`}>{dashboard.lastCallAt ? formatDayLabel(dashboard.lastCallAt) : <Pending />}</td>
      <td className={`${tdClass} text-etn-text-soft`}>{nextCallLabel(dashboard.nextCallAt) ?? <NotSet />}</td>
      <td className={`${tdClass} text-etn-text-soft`}>{dashboard.cadence ? CADENCE_LABELS[dashboard.cadence] : <NotSet />}</td>
      <td className={`${tdClass} text-etn-text-soft`}>
        <RenewalPill companyId={dashboard.companyId} showDate compact empty={<Pending />} />
      </td>
    </tr>
  )
}

export function ClientListView() {
  const [search, setSearch] = useState('')
  const [mine, setMine] = useState(false)
  const { data, isLoading, isError, refetch } = useClientDashboards(mine)
  const unplaced = useUnplacedMeetings().data?.data ?? []
  const everyClient = useClientDashboards(false).data?.data ?? []
  const [assigning, setAssigning] = useState(false)

  const dashboards = data?.data ?? []
  const visible = dashboards.filter(dashboard => dashboard.companyName.toLowerCase().includes(search.trim().toLowerCase()))
  const addButton = (
    <Button size="sm">
      <Plus className="h-4 w-4" />
      Add client
    </Button>
  )

  return (
    <div style={CLIENTS_PAGE}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-etn-text">Client dashboards</h1>
          <p className="mt-1 text-sm text-etn-text-muted">Every client we look after, and what is outstanding with each</p>
        </div>
        <AddClientDialog trigger={addButton} />
      </div>

      {unplaced.length > 0 && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-etn-card border border-etn-border-hairline bg-etn-accent-tint px-[18px] py-2.5">
          <span className="text-[13px] text-etn-text">Some Fireflies calls include an Etainabl user but match no client.</span>
          <Button size="sm" onClick={() => setAssigning(true)}>
            {needsAssigningLabel(unplaced.length)}
          </Button>
          <AssignMeetingDialog
            meetings={unplaced}
            clients={everyClient.map(dashboard => ({ companyId: dashboard.companyId, name: dashboard.companyName }))}
            allowDismiss
            open={assigning}
            onOpenChange={setAssigning}
          />
        </div>
      )}

      <div className="mb-5 flex flex-wrap items-center gap-2.5">
        <div className="relative min-w-[200px] max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-etn-text-soft" />
          <Input pill value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients" aria-label="Search clients" className="pl-9" />
        </div>
        <select aria-label="Which clients" className={filterSelectClass} value={mine ? 'mine' : 'all'} onChange={e => setMine(e.target.value === 'mine')}>
          <option value="all">All clients</option>
          <option value="mine">My clients</option>
        </select>
      </div>

      {isError && !data ? (
        <LoadError title="Client dashboards didn't load" onRetry={() => refetch()} />
      ) : !isLoading && !dashboards.length ? (
        <EmptyState
          icon={<Building2 />}
          title="No clients yet"
          body={mine ? "You aren't on any client's account team yet." : 'Add the clients we look after to give each one a dashboard.'}
        />
      ) : (
        <div className={tableCardClass}>
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr>
                {COLUMNS.map(column => (
                  <th key={column} scope="col" className={thClass}>
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-etn-border-row">
              {visible.map(dashboard => (
                <ClientRow key={dashboard._id} dashboard={dashboard} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
